// The interiors director in the sim (gate C4), as the "places" module on the valley's machinery, and the places lab as
// a variant of the valley (`?scene=places`) built on it. The default scene (the streamed valley, src/world/) mounts the
// same module over its houses' rooms (phase C joined):
//   valley:controllers (order 0)    the hero steps; while a door transition runs, its move is held (a zero drive);
//   valley:physics step (order 100)
//   places:interiors   (order 120)  the director steps with the hero's feet: rooms entered and left, doors that fade,
//                                   and at full black the hero moved through the door (`warpHero`).
// The director's state is a kernel component (saved and hashed), so a replay or a restored save walks through the same
// doors on the same steps.
import { Interiors, initialInteriors, type InteriorEvent, type InteriorSpec, type InteriorsState, type TransitionDoor } from "@rpg/world";
import type { GameModule, World } from "@rpg/kernel";
import { VALLEY_LAYERS, type Course } from "../valley/course";
import type { ValleyExtension, ValleyRig, ValleyVariant } from "../valley/scene";
import type { GameEvents } from "../core";
import { PLACES_FAULTS } from "./faults";
import { PLACES_MOUNT_SPAWN, PLACES_SPAWN, type PlacesWorld } from "./layout";
import type { PlacesContent } from "./content";

export const PLACES_C = "places/State";

/** The places module's state (a kernel component). */
export interface PlacesState {
  interiors: InteriorsState;
  /** Steps since the lab began. */
  step: number;
}

/** A fresh state (a new game, or a save from before the module existed). */
export const freshPlacesState = (): PlacesState => ({ interiors: initialInteriors(), step: 0 });

/** One interiors event as the lab logs it (the sim step it happened on). */
export interface PlacesEvent {
  step: number;
  type: InteriorEvent["type"];
  id: string;
}

/** What the variant exposes once its world is built. */
export interface PlacesSim {
  rig(): ValleyRig;
  state(): PlacesState;
  /** The director (its specs; its state mirrors the component after each step). */
  interiors: Interiors;
  /** The last 200 events, oldest first. */
  log: PlacesEvent[];
  /** The fade's opacity now (0 clear, 1 black). */
  fade(): number;
}

/** The director's numbers (the places-look table's `interiors`). */
export interface InteriorsNumbers {
  exitMargin: number;
  fadeOut: number;
  fadeIn: number;
}

/** The interiors director as a kernel module over a valley rig: rooms (seamless or transition) and transition doors.
 *  Add `module` to the scene's modules, call `build` in its content build, hash `hashed`, and let `heroDrive` hold the
 *  hero while a door fades. */
export function createInteriorsModule(o: { rig: ValleyRig; interiors: readonly InteriorSpec[]; doors: readonly TransitionDoor[]; nums: InteriorsNumbers }): Omit<ValleyExtension, "modules"> & { module: GameModule<GameEvents>; sim: PlacesSim } {
  const { rig, nums } = o;
  let world: World | null = null;
  const interiors = new Interiors({ interiors: [...o.interiors], doors: [...o.doors] }, { exitMargin: nums.exitMargin, fadeOut: nums.fadeOut, fadeIn: nums.fadeIn });
  const state = (): PlacesState => {
    const e = world!.query(PLACES_C)[0];
    return world!.getComponent<PlacesState>(e!, PLACES_C)!;
  };
  const log: PlacesEvent[] = [];
  const module: GameModule<GameEvents> = {
    id: "places",
    version: "0.1.0",
    dependencies: ["valley"],
    setup(ctx) {
      ctx.registerSystem(
        ({ dt }) => {
          const s = state();
          s.step++;
          // the planted defects sit beside what they break
          rig.collision.faults.waterBoxOnly = PLACES_FAULTS["places-water-box"];
          interiors.faults.noWarp = PLACES_FAULTS["places-no-warp"];
          interiors.restore(s.interiors);
          const h = rig.hero();
          for (const e of interiors.step({ x: h.feet.x, y: h.feet.y, z: h.feet.z }, dt)) {
            if (e.type === "warp") rig.warpHero(e.to.x, e.to.y, e.to.z, e.to.yaw);
            log.push({ step: s.step, type: e.type, id: e.type === "enter" || e.type === "exit" ? e.interior.id : e.door.id });
            if (log.length > 200) log.shift();
          }
          s.interiors = interiors.state();
        },
        { name: "places:interiors", order: 120 },
      );
      ctx.registerSaveSlice({
        serialize: () => structuredClone(state()),
        deserialize: (data) => {
          // a save from before the module joined its scene carries a fresh slice (the v5 migration)
          const d = data as Partial<PlacesState> | null;
          Object.assign(state(), d && d.interiors ? structuredClone(d) : freshPlacesState());
          interiors.restore(state().interiors);
        },
      });
    },
  };
  return {
    module,
    hashed: [PLACES_C],
    build(wd) {
      world = wd;
      wd.addComponent<PlacesState>(wd.createEntity(), PLACES_C, freshPlacesState());
    },
    // a door transition holds the hero still (and keeps a jump press in the buffer)
    heroDrive: () => (world && state().interiors.transit ? { x: 0, z: 0 } : null),
    sim: { rig: () => rig, state, interiors, log, fade: () => interiors.fade() },
  };
}

/** The places lab's variant of the valley. `ready` is called with the sim parts once the valley's rig exists. */
export function placesVariant(o: { world: PlacesWorld; content: PlacesContent; ready(s: PlacesSim): void }): ValleyVariant {
  const w = o.world;
  const course = (): Course => ({ bodies: w.bodies, water: w.water, steps: [], ramps: [], stepLaneZ: 0 });
  return {
    name: "places",
    layers: VALLEY_LAYERS,
    course,
    height: (x, z) => w.ground(x, z),
    spawn: PLACES_SPAWN,
    mountSpawn: PLACES_MOUNT_SPAWN,
    extend({ rig }) {
      const m = createInteriorsModule({ rig, interiors: w.interiors, doors: w.doors, nums: o.content.look.interiors });
      o.ready(m.sim);
      return { modules: [m.module], hashed: m.hashed, build: m.build, heroDrive: m.heroDrive };
    },
  };
}
